import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Lock,
  LogOut,
  FileText,
  Download,
  Zap,
  Receipt,
  ShieldCheck,
  User,
  BadgeCheck,
} from "lucide-react";
import Logo from "@/components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rstrom_portal_token";

const inputClass =
  "w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:border-emerald-500 text-sm bg-white";
const labelClass = "block text-xs font-bold text-slate-700 uppercase mb-1";

const MineSiderPage = () => {
  const [epost, setEpost] = useState("");
  const [ordreId, setOrdreId] = useState("");
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Mine sider – Rstrøm";
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (token) fetchProfile(token);
  }, []);

  const fetchProfile = async (token) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/portal/meg`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(res.data);
      setError("");
    } catch (err) {
      sessionStorage.removeItem(TOKEN_KEY);
      setProfile(null);
      if (token !== "login") {
        setError("Økten er utløpt – logg inn på nytt med e-post og ordrenummer.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/portal/login`, {
        epost: epost.trim(),
        ordre_id: ordreId.trim(),
      });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      await fetchProfile(res.data.token);
      toast.success(`Velkommen, ${res.data.navn}!`);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Innlogging feilet. Sjekk e-postadresse og ordrenummer."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setProfile(null);
    setEpost("");
    setOrdreId("");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]" data-testid="mine-sider-page">
      <header className="sticky top-0 z-50 bg-[#0B132B]/95 backdrop-blur-md border-b border-white/10 text-white">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="/" aria-label="Til forsiden" data-testid="mine-sider-logo-link">
            <Logo />
          </a>
          <div className="flex items-center gap-4">
            {profile && (
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-emerald-400 transition-colors"
                data-testid="portal-logout-button"
              >
                <LogOut className="w-4 h-4" /> Logg ut
              </button>
            )}
            <a
              href="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-emerald-400 transition-colors"
              data-testid="mine-sider-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> Til forsiden
            </a>
          </div>
        </div>
      </header>

      {!profile ? (
        <section className="py-20">
          <div className="max-w-md mx-auto px-6">
            <div className="text-center mb-8">
              <h1 className="font-serif-heading text-3xl sm:text-4xl font-bold text-[#0B132B] mb-3">
                Mine sider
              </h1>
              <p className="text-slate-600 text-sm leading-relaxed">
                Logg inn med e-postadressen og ordrenummeret fra bestillingen din.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl border-t-4 border-t-emerald-500">
              <form className="space-y-4" onSubmit={handleLogin} data-testid="portal-login-form">
                <div>
                  <label htmlFor="p_epost" className={labelClass}>E-postadresse</label>
                  <input
                    id="p_epost"
                    type="email"
                    required
                    placeholder="navn@epost.no"
                    className={inputClass}
                    value={epost}
                    onChange={(e) => setEpost(e.target.value)}
                    data-testid="portal-email-input"
                  />
                </div>
                <div>
                  <label htmlFor="p_ordre" className={labelClass}>Ordrenummer</label>
                  <input
                    id="p_ordre"
                    type="text"
                    required
                    placeholder="RS-XXXXXXXX"
                    className={inputClass}
                    value={ordreId}
                    onChange={(e) => setOrdreId(e.target.value)}
                    data-testid="portal-order-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Du finner ordrenummeret i e-posten du fikk da du bestilte.
                  </p>
                </div>

                {error && (
                  <div
                    className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700"
                    data-testid="portal-error"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-[#0B132B] font-bold text-base transition-colors shadow-md flex items-center justify-center gap-2"
                  data-testid="portal-login-button"
                >
                  <Lock className="w-5 h-5" />
                  {loading ? "Logger inn..." : "Logg inn"}
                </button>
              </form>

              <div className="mt-6 p-4 rounded-lg bg-sky-50 border border-sky-200 flex items-start gap-3" data-testid="portal-bankid-note">
                <ShieldCheck className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
                <p className="text-xs text-sky-900 leading-relaxed">
                  <strong>Innlogging med BankID kommer snart.</strong> Da hentes
                  forbruk og målerinformasjon automatisk. Frem til da logger du
                  inn med e-post og ordrenummer.
                </p>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-14">
          <div className="max-w-5xl mx-auto px-6" data-testid="portal-dashboard">
            <div className="mb-10">
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">
                Mine sider
              </span>
              <h1 className="font-serif-heading text-3xl sm:text-4xl font-bold text-[#0B132B] mt-2" data-testid="portal-greeting">
                Hei, {profile.navn.split(" ")[0]}!
              </h1>
              <p className="text-slate-600 text-sm mt-1">
                Her får du oversikt over avtalen din og dokumentene dine.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Min avtale */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7" data-testid="portal-agreement-card">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h2 className="text-lg font-bold text-[#0B132B]">Min avtale</h2>
                </div>
                <dl className="space-y-3 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Produkt</dt>
                    <dd className="font-semibold text-right">{profile.produkt}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Ordrenummer</dt>
                    <dd className="font-semibold" data-testid="portal-order-id">{profile.ordre_id}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Bestilt</dt>
                    <dd className="font-semibold">{profile.dato}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Status</dt>
                    <dd>
                      <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold" data-testid="portal-status">
                        {profile.status}
                      </span>
                    </dd>
                  </div>
                  <div className="border-t border-slate-100 pt-3 flex justify-between gap-4">
                    <dt className="text-slate-500">Påslag</dt>
                    <dd className="font-semibold text-emerald-600">{profile.pris.paslag}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Fast månedsavgift</dt>
                    <dd className="font-semibold">{profile.pris.fastavgift}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Bindingstid</dt>
                    <dd className="font-semibold text-emerald-600">{profile.pris.bindingstid}</dd>
                  </div>
                  {profile.adresse && (
                    <div className="border-t border-slate-100 pt-3 flex justify-between gap-4">
                      <dt className="text-slate-500">Leveringsadresse</dt>
                      <dd className="font-semibold text-right">
                        {profile.adresse}, {profile.postnummer} {profile.poststed}
                      </dd>
                    </div>
                  )}
                  {profile.maalepunkt_id && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-slate-500">Målepunkt-ID</dt>
                      <dd className="font-semibold">{profile.maalepunkt_id}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Dokumenter */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7" data-testid="portal-documents-card">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-sky-600" />
                  </div>
                  <h2 className="text-lg font-bold text-[#0B132B]">Dokumenter</h2>
                </div>
                <p className="text-xs text-slate-500 mb-4">
                  Dokumentene ble også sendt til {profile.epost} da du bestilte.
                </p>
                <div className="space-y-3">
                  {profile.dokumenter.map((d) => (
                    <a
                      key={d.id}
                      href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-emerald-500 transition-colors group"
                      data-testid={`portal-doc-${d.id}`}
                    >
                      <span className="flex items-center gap-3 text-sm font-semibold text-[#0B132B]">
                        <FileText className="w-5 h-5 text-slate-400 group-hover:text-emerald-600 transition-colors" />
                        {d.tittel}
                      </span>
                      <Download className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 transition-colors" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Forbruk */}
              <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-7" data-testid="portal-forbruk-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-slate-400" />
                  </div>
                  <h2 className="text-lg font-bold text-[#0B132B]">Forbruk</h2>
                  <span className="ml-auto px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase" data-testid="portal-forbruk-badge">
                    Kommer snart
                  </span>
                </div>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Time-for-time forbruksdata hentes automatisk fra Elhub når
                  BankID-innlogging er på plass. Da ser du forbruket ditt her –
                  måned for måned.
                </p>
              </div>

              {/* Fakturaer */}
              <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-7" data-testid="portal-faktura-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-slate-400" />
                  </div>
                  <h2 className="text-lg font-bold text-[#0B132B]">Fakturaer</h2>
                  <span className="ml-auto px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase" data-testid="portal-faktura-badge">
                    Kommer snart
                  </span>
                </div>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Fakturaene dine samles her når fakturasystemet er koblet til.
                  Frem til da mottar du faktura på e-post som vanlig.
                </p>
              </div>
            </div>

            <div className="mt-8 p-5 rounded-2xl bg-[#0B132B] text-white flex items-start gap-4" data-testid="portal-help-banner">
              <BadgeCheck className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-sm text-slate-300 leading-relaxed">
                Spørsmål om avtalen din? Svar på e-posten du fikk da du bestilte,
                eller skriv til{" "}
                <a href="mailto:post@rstrom.no" className="text-emerald-400 font-semibold hover:underline" data-testid="portal-help-email">
                  post@rstrom.no
                </a>
                . Oppgi ordrenummeret ditt ({profile.ordre_id}).
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default MineSiderPage;
